import { Outlet, useNavigate } from "react-router";

import { PageContextProvider } from "@digital-services/shell";
import {
  AlertProvider,
  DialogProvider,
  DrawerProvider,
  Sidebar,
  useSidebar
} from "@digital-services/ui";

import Header from "@components/Header/Header";
import { FooterNavigation } from "@components/navigation/FooterNavigation";
import { MenuNavigation } from "@components/navigation/MenuNavigation";
import RequireAuth from "@components/RequireAuth";

import UserContextProvider from "./hooks/useUserContext/UserContextProvider";

const appName = import.meta.env.VITE_APP_APPLICATION_NAME || "";

export const Root = () => {
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();

  // base class names
  const baseClasses =
    "flex grow flex-col overflow-auto pb-[102px] pt-[9px] transition-all duration-200";
  // small screen class names
  const smallScreenClasses = "px-16";
  // large screen class names
  const largeScreenClasses =
    "large-screen:pl-[calc((100vw-1560px)/2)] large-screen:pr-[calc((100vw-1560px)/2)]";
  // collapsed class names
  const collapsedClasses = isSidebarCollapsed
    ? "small-screen:pl-[72px] small-screen:pr-16"
    : "small-screen:pl-6 small-screen:pr-8 small-screen:ml-12";

  const cssClasses = `${baseClasses} ${smallScreenClasses} ${largeScreenClasses} ${collapsedClasses}`;

  // TBD try again later
  // Note that if we set readable value for `applicationName` for `<PageContextProvider>` it will "blink" while authentication on the top-left corner
  // Setting empty value "", because component requires, helps to avoid blinking, but then on other pages `<title>` is EMPTY`.

  return (
    <div
      data-testid="page-content"
      className={`flex h-screen ${isSidebarCollapsed ? "ml-[36px]" : "ml-[150px] pl-0"}`}
    >
      <PageContextProvider applicationName={appName}>
        <RequireAuth>
          <UserContextProvider>
            <AlertProvider>
              <DialogProvider>
                <DrawerProvider>
                  <Sidebar
                    menuItems={<MenuNavigation />}
                    footerItems={<FooterNavigation />}
                    onClickBranding={() => navigate("/")}
                  />
                  <main className={cssClasses}>
                    <Header />
                    <Outlet />
                  </main>
                </DrawerProvider>
              </DialogProvider>
            </AlertProvider>
          </UserContextProvider>
        </RequireAuth>
      </PageContextProvider>
    </div>
  );
};
